import {
    Building,
    CheckCircle,
    Clock,
    Edit,
    Eye,
    FileText,
    Mail,
    Phone,
    Plus,
    Search,
    Trash2,
    X,
    XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

interface MobilisationPartner {
    mp_id: number;
    name: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    gst_number?: string;
    status: string;
    metadata?: any;
    created_at: string;
    _count?: {
        centers: number;
        trainers: number;
        mobilisations: number;
        users: number;
    };
}

// Simple validators
const validateEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const validatePhone = (phone: string) => {
  // Simple 10-digit validation
  const regex = /^\d{10}$/;
  return regex.test(phone);
};

const validateGST = (gst: string) => {
  // GSTIN: 15 characters, alphanumeric, specific pattern
  const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!regex.test(gst)) {
    return {
      valid: false,
      reason:
        "Format invalid: use SSAAAAANNNNAEZC (e.g., 27AAPFU0939F1ZV). " +
        "Starts with 2 digits (state), then 5 letters, 4 digits, 1 letter, 1 alpha-num, 'Z', 1 alpha-num.",
    };
  }
   return { valid: true };
};

const MobilisationPartnerManagement: React.FC = () => {
    const { user } = useAuth();
    const [partners, setPartners] = useState<MobilisationPartner[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showViewModal, setShowViewModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState<MobilisationPartner | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    // const [showReviewModal, setShowReviewModal] = useState(false);
    // const [reviewAction, setReviewAction] = useState<'approved' | 'rejected'>('approved');
    // const [reviewComments, setReviewComments] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        gst_number: '',
        status: ''
    });
    const [formErrors, setFormErrors] = useState({
  email: '',
  phone: '',
  gst: ''
});
    const VALID_PARTNER_STATUSES = ['Active', 'Inactive'];

    useEffect(() => {
        if (user?.role === 'SYSTEM_ADMIN') {
            fetchPartners();
        } else {
            console.log('User is not system admin, access denied');
        }
    }, [statusFilter, user]);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/partners?status=${statusFilter}`);

            if (response.data.success) {
                setPartners(response.data.data);
            }
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to fetch parnters.");
            console.error('Failed to fetch mobilisation partners:', error);
        } finally {
            setLoading(false);
        }
    };

    // const handleReviewPartner = async (e: React.FormEvent) => {
    //     e.preventDefault();
    //     if (!selectedPartner) return;

    //     try {
    //         const response = await api.post(`/partners/${selectedPartner.mp_id}/review`, {
    //             action: reviewAction,
    //             comments: reviewComments
    //         });

    //         if (response.data.success) {
    //             setShowReviewModal(false);
    //             setReviewComments('');
    //             fetchPartners();
    //             alert(`Partner ${reviewAction} successfully!`);
    //         }
    //     } catch (error: any) {
    //         console.error('Failed to review partner:', error);
    //         const errorMessage = error.response?.data?.message || 'Failed to review partner';
    //         alert(`Error: ${errorMessage}`);
    //     }
    // };

    const handleCreatePartner = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedData = {
            ...formData,
            contact_email: formData.contact_email.trim(),
            contact_phone: formData.contact_phone.trim(),
            gst_number: formData.gst_number.trim(),
            name: formData.name.trim(),
            address: formData.address.trim(),
        };

        let errors: any = {};
        if (trimmedData.contact_email && !validateEmail(trimmedData.contact_email)) {
            errors.email = "Invalid email format";
        }
        if (trimmedData.contact_phone && !validatePhone(trimmedData.contact_phone)) {
            errors.phone = "Phone must be 10 digits";
        }
        if (trimmedData.gst_number && trimmedData.gst_number.trim() && !validateGST(trimmedData.gst_number)) {
            errors.gst = "Invalid GST number format";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({ email: '', phone: '', gst: '' });

        try {
            setError(null);
            setSubmitLoading(true);

            const response = await api.post('/partners', trimmedData);

            if (response.data.success) {
                setSuccess("Partner has been created successfully.");
                setShowCreateModal(false);
                fetchPartners();
                resetForm();
            } 
        } catch (error: any) {
            console.error('Failed to create partner:', error);
            const errorMessage = error.response?.data?.message || 'Failed to create partner';
            setError(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdatePartner = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedPartner) return;

        const trimmedData = {
            ...formData,
            contact_email: formData.contact_email.trim(),
            contact_phone: formData.contact_phone.trim(),
            gst_number: formData.gst_number.trim(),
            name: formData.name.trim(),
            address: formData.address.trim(),
        };

        let errors: any = {};
        if (trimmedData.contact_email && !validateEmail(trimmedData.contact_email)) {
            errors.email = "Invalid email format";
        }
        if (trimmedData.contact_phone && !validatePhone(trimmedData.contact_phone)) {
            errors.phone = "Phone must be 10 digits";
        }
        if (trimmedData.gst_number && trimmedData.gst_number.trim() && !validateGST(trimmedData.gst_number)) {
            errors.gst = "Invalid GST number format";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({ email: '', phone: '', gst: '' });


        try {
            setError(null);
            setSubmitLoading(true);

            const response = await api.put(`/partners/${selectedPartner.mp_id}`, trimmedData);

            if (response.data.success) {
                setSuccess("Partner updated successfully");
                setShowEditModal(false);
                fetchPartners();
                resetForm();
            }
        } catch (error: any) {
            console.error('Failed to update partner:', error);
            const errorMessage = error.response?.data?.message || 'Failed to update partner';
            setError(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDeletePartner = async (partnerId: number) => {
        if (!confirm('Are you sure you want to delete this partner?')) return;

        try {
            setError(null);
            setLoading(true);
            const response = await api.delete(`/partners/${partnerId}`);

            if (response.data.success) {
                setSuccess("Partner deleted successfully.");
                fetchPartners();
            }
        } catch (error: any) {
            console.error('Failed to delete partner:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete partner';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            contact_email: '',
            contact_phone: '',
            address: '',
            gst_number: '',
            status: '',
        });
        setSelectedPartner(null);
        setFormErrors({ email: '', phone: '', gst: '' });
        setError(null);
        setSubmitLoading(false);
    };

    const openEditModal = (partner: MobilisationPartner) => {
        setError(null);
        setFormErrors({ email: '', phone: '', gst: '' });
        setSubmitLoading(false);
        setSelectedPartner(partner);
        setFormData({
            name: partner.name,
            contact_email: partner.contact_email || '',
            contact_phone: partner.contact_phone || '',
            address: partner.address || '',
            gst_number: partner.gst_number || '',
            status: partner.status
        });
        setShowEditModal(true);
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Draft':
                return <Clock className="w-4 h-4 text-gray-500" />;
            case 'Pending':
                return <Clock className="w-4 h-4 text-blue-500" />;
            case 'Active':
            case 'Approved':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'Rejected':
                return <XCircle className="w-4 h-4 text-red-500" />;
            case 'Inactive':
                return <XCircle className="w-4 h-4 text-gray-500" />;
            default:
                return <Clock className="w-4 h-4 text-gray-500" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Draft':
                return 'bg-gray-100 text-gray-800';
            case 'Pending':
                return 'bg-blue-100 text-blue-800';
            case 'Active':
            case 'Approved':
                return 'bg-green-100 text-green-800';
            case 'Rejected':
                return 'bg-red-100 text-red-800';
            case 'Inactive':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredPartners = partners.filter(partner =>
        partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partner.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partner.gst_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!user || user.role !== 'SYSTEM_ADMIN') {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-gray-600">Only system administrators can access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    const isModalOpen = showCreateModal || showEditModal || showViewModal;

    return (
        //  className="space-y-6"
        <div>
            {/* Notifications */}
            {error && !isModalOpen && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setError(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{success}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setSuccess(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Mobilisation Partner Management</h1>
                    <p className="text-gray-600">Manage and approve mobilisation partner organizations</p>
                </div>
                <button
                    onClick={() => { setError(null); setFormErrors({ email: '', phone: '', gst: '' }); setShowCreateModal(true); }}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Partner
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search partners..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="sm:w-48">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                            <option value="">All Status</option>
                            {/* <option value="Draft">Draft</option> */}
                            {/* <option value="Pending">Pending</option> */}
                            <option value="Active">Active</option>
                            {/* <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option> */}
                            <option value="Inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Partners Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredPartners.map((partner) => (
                    <div
                        key={partner.mp_id}
                        className="h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                                    {partner.name}
                                </h3>
                                <div className="flex items-center gap-2">
                                    {getStatusIcon(partner.status)}
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(partner.status)}`}>
                                        {partner.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="space-y-3 mb-4">
                            {partner.contact_email && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Mail className="w-4 h-4" />
                                    <span>{partner.contact_email}</span>
                                </div>
                            )}
                            {partner.contact_phone && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Phone className="w-4 h-4" />
                                    <span>{partner.contact_phone}</span>
                                </div>
                            )}
                            {partner.gst_number && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <FileText className="w-4 h-4" />
                                    <span>GST: {partner.gst_number}</span>
                                </div>
                            )}
                            {partner._count && (
                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 pt-2 border-t">
                                    <div>Centers: {partner._count.centers}</div>
                                    <div>Trainers: {partner._count.trainers}</div>
                                    <div>Mobilisations: {partner._count.mobilisations}</div>
                                    <div>Users: {partner._count.users}</div>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="mt-auto flex flex-col gap-2 pt-2">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setSelectedPartner(partner);
                                        setShowViewModal(true);
                                    }}
                                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 flex items-center justify-center gap-1"
                                >
                                    <Eye className="w-4 h-4" />
                                    View
                                </button>
                                <button
                                    onClick={() => openEditModal(partner)}
                                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                                >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                </button>
                                {/* <button
                                    onClick={() => handleDeletePartner(partner.mp_id)}
                                    className="flex-1 bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 flex items-center justify-center gap-1"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button> */}
                            </div>
                            {/* Admin Review Actions */}
                            {/* {(() => {
                                console.log('Partner status:', partner.status, 'User role:', user?.role);
                                // Temporarily show for all statuses for debugging
                                return user?.role === 'SYSTEM_ADMIN';
                            })() && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setSelectedPartner(partner);
                                            setReviewAction('approved');
                                            setShowReviewModal(true);
                                        }}
                                        className="flex-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 flex items-center justify-center gap-1"
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedPartner(partner);
                                            setReviewAction('rejected');
                                            setShowReviewModal(true);
                                        }}
                                        className="flex-1 bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 flex items-center justify-center gap-1"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Reject
                                    </button>
                                </div>
                            )} */}
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {filteredPartners.length === 0 && (
                <div className="text-center py-12">
                    <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No partners found</h3>
                    <p className="text-gray-600 mb-4">No mobilisation partners match your current filters.</p>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && selectedPartner && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">{selectedPartner.name}</h2>
                                    <div className="flex items-center gap-2 mt-2">
                                        {getStatusIcon(selectedPartner.status)}
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPartner.status)}`}>
                                            {selectedPartner.status}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowViewModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Basic Information */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                                <div className="space-y-3">
                                    {selectedPartner.contact_email && (
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Email</span>
                                            <p className="text-gray-900">{selectedPartner.contact_email}</p>
                                        </div>
                                    )}
                                    {selectedPartner.contact_phone && (
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Phone</span>
                                            <p className="text-gray-900">{selectedPartner.contact_phone}</p>
                                        </div>
                                    )}
                                    {selectedPartner.gst_number && (
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">GST Number</span>
                                            <p className="text-gray-900">{selectedPartner.gst_number}</p>
                                        </div>
                                    )}
                                    {selectedPartner.address && (
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Address</span>
                                            <p className="text-gray-900">{selectedPartner.address}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Statistics */}
                            {selectedPartner._count && (
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <span className="text-sm font-medium text-gray-500">Centers</span>
                                            <p className="text-2xl font-bold text-gray-900">{selectedPartner._count.centers}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <span className="text-sm font-medium text-gray-500">Trainers</span>
                                            <p className="text-2xl font-bold text-gray-900">{selectedPartner._count.trainers}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <span className="text-sm font-medium text-gray-500">Mobilisations</span>
                                            <p className="text-2xl font-bold text-gray-900">{selectedPartner._count.mobilisations}</p>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <span className="text-sm font-medium text-gray-500">Users</span>
                                            <p className="text-2xl font-bold text-gray-900">{selectedPartner._count.users}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Review Information */}
                            {selectedPartner.metadata?.review_comments && (
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Review Information</h3>
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <p className="text-gray-800">{selectedPartner.metadata.review_comments}</p>
                                        {selectedPartner.metadata.reviewed_at && (
                                            <p className="text-sm text-gray-500 mt-2">
                                                Reviewed on {new Date(selectedPartner.metadata.reviewed_at).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* Review Modal
            {showReviewModal && selectedPartner && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                {reviewAction === 'approved' ? 'Approve' : 'Reject'} Partner
                            </h2>
                            <p className="text-gray-600 mt-1">{selectedPartner.name}</p>
                        </div>

                        <form onSubmit={handleReviewPartner} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {reviewAction === 'approved' ? 'Approval Comments (Optional)' : 'Rejection Reason *'}
                                </label>
                                <textarea
                                    rows={4}
                                    value={reviewComments}
                                    onChange={(e) => setReviewComments(e.target.value)}
                                    required={reviewAction === 'rejected'}
                                    placeholder={
                                        reviewAction === 'approved'
                                            ? 'Add any approval notes...'
                                            : 'Please provide a reason for rejection...'
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowReviewModal(false);
                                        setReviewComments('');
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={`px-4 py-2 text-white rounded-lg hover:opacity-90 ${reviewAction === 'approved'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    {reviewAction === 'approved' ? 'Approve Partner' : 'Reject Partner'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )} */}

            {/* Create Modal */}
            {showCreateModal && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">Create New Partner</h2>
                        </div>

                        {error && showCreateModal && (
                            <div className="mx-6 mt-4 mb-2 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <span className="flex-1">{error}</span>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => setError(null)}
                                    aria-label="Dismiss error"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleCreatePartner} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Partner Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Email *
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={formData.contact_email}
                                    onChange={(e) => {
                                        setFormData({ ...formData, contact_email: e.target.value });
                                        setFormErrors((prev) => ({ ...prev, email: '' }));
                                        setError(null);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Phone *
                                </label>
                                <input
                                    type="tel"
                                    required
                                    value={formData.contact_phone}
                                    onChange={(e) => {
                                        setFormData({ ...formData, contact_phone: e.target.value });
                                        setFormErrors((prev) => ({ ...prev, phone: '' }));
                                        setError(null);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                {formErrors.phone && <p className="text-red-500 text-sm">{formErrors.phone}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address *
                                </label>
                                <textarea
                                    rows={3}
                                    required
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    GST Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.gst_number}
                                    onChange={(e) => {
                                        setFormData({ ...formData, gst_number: e.target.value });
                                        setFormErrors((prev) => ({ ...prev, gst: '' }));
                                        setError(null);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                {formErrors.gst && <p className="text-red-500 text-sm">{formErrors.gst}</p>}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <span className="flex items-center gap-2">
                                        {submitLoading && showCreateModal && (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                        )}
                                        {submitLoading && showCreateModal ? 'Creating...' : 'Create Partner'}
                                    </span>
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedPartner && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">Edit Partner</h2>
                        </div>

                        {error && showEditModal && (
                            <div className="mx-6 mt-4 mb-2 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <span className="flex-1">{error}</span>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => setError(null)}
                                    aria-label="Dismiss error"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleUpdatePartner} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Partner Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Email
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={formData.contact_email}
                                    onChange={(e) => {
                                        setFormData({ ...formData, contact_email: e.target.value });
                                        setFormErrors((prev) => ({ ...prev, email: '' }));
                                        setError(null);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Phone
                                </label>
                                <input
                                    type="tel"
                                    required
                                    value={formData.contact_phone}
                                    onChange={(e) => {
                                        setFormData({ ...formData, contact_phone: e.target.value });
                                        setFormErrors((prev) => ({ ...prev, phone: '' }));
                                        setError(null);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                {formErrors.phone && <p className="text-red-500 text-sm">{formErrors.phone}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address
                                </label>
                                <textarea
                                    rows={3}
                                    required
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    GST Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.gst_number}
                                    onChange={(e) => {
                                        setFormData({ ...formData, gst_number: e.target.value });
                                        setFormErrors((prev) => ({ ...prev, gst: '' }));
                                        setError(null);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                {formErrors.gst && <p className="text-red-500 text-sm">{formErrors.gst}</p>}
                            </div>

                            <div className="sm:w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    {VALID_PARTNER_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <span className="flex items-center gap-2">
                                        {submitLoading && showEditModal && (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                        )}
                                        {submitLoading && showEditModal ? 'Updating...' : 'Update Partner'}
                                    </span>
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
};

export default MobilisationPartnerManagement;
